import * as React from "react";
import { I18n } from "react-redux-i18n";

import { QuestionnaireAutomaticReminderSettings } from "@common/dto/questionnaire/emails-tab";
import { QuestionnaireAutoReminderFrequencyType } from "@common/dto/questionnaire/frequency";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { QuestionnaireFolderField } from "@modules/fhq-design-tool/types/field";
import { MessageConfirmParams } from "@common/hooks/message";
import { InfoMessage } from "@common/components/info-message";
import { Title } from "@modules/fhq-design-tool/components/questionnaire-management/components/title";
import { Checkbox } from "@modules/fhq-design-tool/components/questionnaire-management/components/checkbox";
import { TextField } from "@modules/fhq-design-tool/components/questionnaire-management/components/text-field";
import { Select } from "@modules/fhq-design-tool/components/questionnaire-management/components/select";
import {
  FieldPicker,
  FieldPickerOnChangeValue,
} from "@modules/fhq-design-tool/components/questionnaire-management/components/field-picker";
import { QuestionnaireUpdateLocalDto } from "@modules/fhq-design-tool/dto/questionnaire-update-local-dto";
import { QuestionnaireManagementErrors } from "@modules/fhq-design-tool/controllers/questionnaire-validation";
import { FieldType } from "@common/dto/field/type";
import { QuestionnaireCreateLocalDto } from "@modules/fhq-design-tool/dto/questionnaire-create-local-dto";

import "./automatic-reminder-settings.styles.scss";

type AutomaticReminderSettingsProps = {
  questionnaire: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto;
  onChange: (questionnaire: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto) => void;
  getFolders: () => Promise<Array<FolderPrimaryListDto>>;
  getFolderFields: (params: {
    folderId?: FolderPrimaryListDto["id"];
    searchQuery?: string;
  }) => Promise<Array<QuestionnaireFolderField>>;
  showConfirmMessage: (props: MessageConfirmParams) => void;
  onMakeUncompletedInactiveConfirm: () => void;
  errors?: QuestionnaireManagementErrors;
};

export const AutomaticReminderSettings: React.FC<AutomaticReminderSettingsProps> = (props) => {
  const { questionnaire, onChange, getFolderFields, getFolders, showConfirmMessage, onMakeUncompletedInactiveConfirm, errors } = props;


  const handleShowConfirmMessage = React.useCallback((message: string) => {
    showConfirmMessage({
      header: I18n.t("Confirmation Message"),
      content: (
        <InfoMessage>
          {message}
        </InfoMessage>
      ),
      confirmText: I18n.t("OK"),
      onCustomOk: onMakeUncompletedInactiveConfirm,
    });
  }, [showConfirmMessage, onMakeUncompletedInactiveConfirm]);

  const handleChange = React.useCallback(
    (changedValues: Partial<QuestionnaireAutomaticReminderSettings>) => {
      onChange({
        ...questionnaire,
        emailsTab: {
          ...questionnaire.emailsTab,
          ...changedValues,
        },
      });
    },
    [questionnaire, onChange]
  );

  const handleEnabledAutoRemindersChange = React.useCallback(
    (value: boolean) => {
      if (value && !questionnaire.statusTab?.makeUncompletedInactive) {
        return handleShowConfirmMessage(I18n.t('changingAutomaticRemindersSettingsWarning'));
      }

      const makeInactiveAfterSelected = [
        questionnaire.statusTab?.makeUncompletedInactive,
        questionnaire.statusTab.makeInactiveAfter,
        questionnaire.statusTab.makeInactiveAfterFrequency,
        questionnaire.statusTab.makeInactiveAfterType,
      ].every(Boolean);

      const makeInactiveAfterDateSelected = [
        questionnaire.statusTab?.makeUncompletedInactive,
        questionnaire.statusTab?.makeInactiveAfterDate,
        questionnaire.statusTab.makeInactiveAfterDateFieldName,
      ].every(Boolean);

      const requiredFieldsForEnabledAutoRemindersSelected = [makeInactiveAfterSelected, makeInactiveAfterDateSelected].some(Boolean);

      if (value && !requiredFieldsForEnabledAutoRemindersSelected) {
        return handleShowConfirmMessage(I18n.t(
          'One or both of the uncompleted/inactive status configurations must be completed in order to turn on Automated Reminders'
        ));
      }

      handleChange({
        enabledAutoReminders: value,
        ...(value ? {} : {
          remindEvery: false,
          remindOnce: false,
          remindEveryValue: 0,
          remindEveryType: null,
          remindEveryMax: 0,
          remindOnceValue: 0,
          remindOnceType: null,
          remindOnceDateFieldName: "",
          remindOnceDateFieldId: 0,
        })
      });
    },
    [questionnaire, handleChange, handleShowConfirmMessage]
  );

  const handleRemindOnceDateFieldIdChange = React.useCallback(
    (value: FieldPickerOnChangeValue | undefined) => {
      onChange({
        ...questionnaire,
        emailsTab: {
          ...questionnaire.emailsTab,
          remindOnceDateFieldId: value ? Number(value.field.id) : undefined,
          remindOnceDateFieldName: value ? value.field.name : undefined,
        },
      });
    },
    [onChange, questionnaire]
  );

  const getFoldersForAfterThisDateField = React.useCallback(() => {
    return getFolders().then((folders) => {
      return folders.filter((x) => !x.system);
    })
  }, [getFolders]);

  const getFieldsForAfterThisDateField: typeof getFolderFields = React.useCallback((...args) => {
    const allowedFieldTypes = [FieldType.date, FieldType.tableSummary, FieldType.computed];
    
    return getFolderFields(...args).then((fields) => {
      return fields.filter((x) => allowedFieldTypes.includes(x.fieldTypeId));
    })
  }, [getFolders]);

  const handleRemindEveryChange = React.useCallback((value: boolean) => {
    const clearedValues = { remindEveryValue: 0, remindEveryType: undefined, remindEveryMax: 0 };
    handleChange({ remindEvery: value, ...(value ? {} : clearedValues) });
  }, [handleChange]);
  
  const handleRemindOnceChange =  React.useCallback((value: boolean) => {
    const clearedValues = { remindOnceValue: 0, remindOnceType: null, remindOnceDateFieldName: '', remindOnceDateFieldId: 0 };
    handleChange({ remindOnce: value, ...(value ? {} : clearedValues) });
  }, [handleChange]);

  const disabledAutoReminders = !questionnaire.emailsTab.enabledAutoReminders;
  const disabledRemindEvery = disabledAutoReminders || !questionnaire.emailsTab.remindEvery;
  const disabledRemindOnce = disabledAutoReminders || !questionnaire.emailsTab.remindOnce;

  return (
    <div className="FhqDesignToolAutomaticReminderSetting">
      <Title margin="0" text={I18n.t("Automated Reminder Settings")} />
      <Checkbox
        value={questionnaire.emailsTab.enabledAutoReminders}
        onChange={handleEnabledAutoRemindersChange}
        metadata={{
          description: "enabled-auto-reminders",
        }}
        label={I18n.t("Turn on Automated Reminders")}
        labelWidth={180}
        margin="8px 0 0 0"
      />
      <div className="FhqDesignToolAutomaticReminderSetting__fields">
        <Checkbox
          className="FhqDesignToolAutomaticReminderSetting__field FhqDesignToolAutomaticReminderSetting__field__remindEvery"
          value={questionnaire.emailsTab.remindEvery}
          onChange={handleRemindEveryChange}
          metadata={{
            description: "remind-every",
          }}
          label={I18n.t("Remind Every")}
          labelWidth={90}
          disabled={disabledAutoReminders}
        />
        <TextField
          className="FhqDesignToolAutomaticReminderSetting__field"
          metadata={{
            description: "remind-every-value",
          }}
          value={(questionnaire.emailsTab.remindEveryValue || "").toString()}
          type="number"
          onChange={(value) => handleChange({ remindEveryValue: Number(value) || 0 })}
          inputWidth={115}
          disabled={disabledRemindEvery}
          min={0}
          max={999}
          errors={errors?.properties?.remindEveryValue}
          classes={{
            errorText: 'FhqDesignToolAutomaticReminderSetting__field__errorText'
          }}
        />
        <Select
          className="FhqDesignToolAutomaticReminderSetting__field"
          options={[
            { value: QuestionnaireAutoReminderFrequencyType.daily, label: I18n.t("Days") },
            { value: QuestionnaireAutoReminderFrequencyType.weekly, label: I18n.t("Weeks") },
          ]}
          value={questionnaire.emailsTab.remindEveryType}
          metadata={{
            description: "remind-every-type",
          }}
          onChange={(value) => handleChange({ remindEveryType: value as QuestionnaireAutoReminderFrequencyType })}
          disabled={disabledRemindEvery}
          errors={errors?.properties?.remindEveryType}
        />
        <TextField
          className="FhqDesignToolAutomaticReminderSetting__field"
          metadata={{
            description: "remind-every-max",
          }}
          value={(questionnaire.emailsTab.remindEveryMax || "").toString()}
          type="number"
          onChange={(value) => handleChange({ remindEveryMax: Number(value) || 0 })}
          label={I18n.t("Max Number of Reminders to Send")}
          inputWidth={115}
          labelWidth={200}
          disabled={disabledRemindEvery}
          min={0}
          max={999}
          errors={errors?.properties?.remindEveryMax}
          classes={{
            errorText: 'FhqDesignToolAutomaticReminderSetting__field__remindEveryMax__errorText'
          }}
        />
      </div>
      <div className="FhqDesignToolAutomaticReminderSetting__fields">
        <Checkbox
          className="FhqDesignToolAutomaticReminderSetting__field FhqDesignToolAutomaticReminderSetting__field__remindOnce"
          value={questionnaire.emailsTab.remindOnce}
          onChange={handleRemindOnceChange}
          metadata={{
            description: "remind-once",
          }}
          label={I18n.t("Remind Once")}
          labelWidth={90}
          disabled={disabledAutoReminders}
        />
        <TextField
          className="FhqDesignToolAutomaticReminderSetting__field"
          metadata={{
            description: "remind-once-value",
          }}
          value={(questionnaire.emailsTab.remindOnceValue || "").toString()}
          type="number"
          onChange={(value) => handleChange({ remindOnceValue: Number(value) || 0 })}
          inputWidth={115}
          disabled={disabledRemindOnce}
          min={0}
          max={999}
          errors={errors?.properties?.remindOnceValue}
          classes={{
            errorText: 'FhqDesignToolAutomaticReminderSetting__field__errorText'
          }}
        />
        <Select
          className="FhqDesignToolAutomaticReminderSetting__field"
          options={[
            { value: QuestionnaireAutoReminderFrequencyType.daily, label: I18n.t("Days") },
            { value: QuestionnaireAutoReminderFrequencyType.weekly, label: I18n.t("Weeks") },
          ]}
          value={questionnaire.emailsTab.remindOnceType}
          metadata={{
            description: "remind-once-type",
          }}
          onChange={(value) => handleChange({ remindOnceType: value as QuestionnaireAutoReminderFrequencyType })}
          disabled={disabledRemindOnce}
          errors={errors?.properties?.remindOnceType}
        />
        <FieldPicker
          className="FhqDesignToolAutomaticReminderSetting__field"
          withSearch={true}
          value={(questionnaire.emailsTab.remindOnceDateFieldName || "").toString()}
          onChange={handleRemindOnceDateFieldIdChange}
          metadata={{
            description: "remind-once-date-field-name",
          }}
          getFields={getFieldsForAfterThisDateField}
          getFolders={getFoldersForAfterThisDateField}
          label={I18n.t("Before this date")}
          labelWidth={100}
          disabled={disabledRemindOnce}
          errors={errors?.properties?.remindOnceDateFieldName}
        />
      </div>
    </div>
  );
};
